// Feedback-Aufnahmen-Queue — überlebt einen Netzabbruch mitten in der Fahrt.
//
// Anders als offlineQueue.ts (die nur Text/JSON-Anfragen stumpf wiederholt):
// eine Aufnahme lässt sich nicht blind erneut senden, sie muss erst wieder
// bei Whisper/der KI ausgewertet werden. Deshalb zwei Töpfe:
//   feedback_pending — Rohaufnahme, noch nicht hochgeladen/ausgewertet
//   feedback_ready   — fertig ausgewertete Einträge, warten auf Bestätigung
//     durch den Menschen (die bleibt Pflicht, auch wenn die Auswertung erst
//     gelang, nachdem die Feedback-Seite längst geschlossen war).
import {
  feedbackPendingAll, feedbackPendingRemove, feedbackPendingAdd,
  feedbackReadyAdd, FeedbackPendingRecording,
} from './db';
import { toast } from 'sonner';

const MAX_RETRIES = 5;
let isProcessing = false;

/** Netzwerkfehler (offline, DNS, Timeout) vs. echter Fehlschlag (z.B. 401/422)
 *  unterscheiden — dieselbe Logik wie isNetworkError() in api.ts, hier separat
 *  gehalten statt importiert, um den Zyklus feedbackQueue ↔ api zu vermeiden. */
function istNetzwerkfehler(err: unknown): boolean {
  if (typeof navigator !== 'undefined' && navigator.onLine === false) return true;
  if (err instanceof TypeError) return true;
  const msg = err instanceof Error ? err.message : '';
  return msg.includes('Failed to fetch') || msg.includes('NetworkError')
    || msg.includes('Load failed') || msg.includes('Internetverbindung');
}

/** Eine neue Aufnahme in die Warteschlange legen — VOR jedem Upload-Versuch,
 *  nicht erst danach: sonst ist sie genau in dem Moment weg, fuer den diese
 *  Funktion gebaut ist. */
export async function queueRecording(blob: Blob): Promise<FeedbackPendingRecording> {
  const entry: FeedbackPendingRecording = {
    id: crypto.randomUUID(), blob, createdAt: Date.now(), retries: 0,
  };
  await feedbackPendingAdd(entry);
  return entry;
}

/**
 * Eine wartende Aufnahme jetzt versuchen hochzuladen/auswerten zu lassen.
 * 'ok' → in feedback_ready abgelegt, die aufrufende Seite liest es von dort.
 * 'wartet' → Netzwerkfehler, bleibt unangetastet in feedback_pending.
 * 'verworfen' → nach MAX_RETRIES echten Fehlschlaegen aufgegeben.
 */
export async function verarbeiteJetzt(
  entry: FeedbackPendingRecording,
): Promise<'ok' | 'wartet' | 'verworfen'> {
  // Dynamischer Import: pcFeedbackExtract lebt in api.ts, das seinerseits
  // (indirekt ueber offlineQueue) auf db.ts zeigt — Zyklus vermeiden.
  const { pcFeedbackExtract } = await import('./api');
  try {
    const result = await pcFeedbackExtract(entry.blob);
    await feedbackPendingRemove(entry.id);
    await feedbackReadyAdd({
      id: entry.id,
      transkript_roh: result.transkript_roh,
      sprache_erkannt: result.sprache_erkannt,
      eintraege: result.eintraege as unknown as Record<string, unknown>[],
      createdAt: entry.createdAt,
    });
    return 'ok';
  } catch (err) {
    if (istNetzwerkfehler(err)) {
      return 'wartet'; // kein Netz ist kein Fehler der Aufnahme, kein Zaehler hoch
    }
    if (entry.retries + 1 >= MAX_RETRIES) {
      await feedbackPendingRemove(entry.id);
      return 'verworfen';
    }
    await feedbackPendingAdd({ ...entry, retries: entry.retries + 1 });
    return 'wartet';
  }
}

/** Alle wartenden Aufnahmen abarbeiten. Nacheinander, nicht parallel — sonst
 *  laufen mehrere Whisper-Aufrufe gleichzeitig los, sobald wieder Netz da ist.
 *  Fuer den Hintergrund-Sync (main.tsx) — die aktuell offene Feedback-Seite
 *  nutzt verarbeiteJetzt() direkt fuer sofortiges Feedback. */
export async function processFeedbackQueue(): Promise<void> {
  if (isProcessing || !navigator.onLine) return;
  isProcessing = true;
  try {
    const wartend = await feedbackPendingAll();
    let verworfen = 0;
    for (const entry of wartend) {
      const status = await verarbeiteJetzt(entry);
      if (status === 'verworfen') verworfen++;
    }
    if (verworfen > 0) {
      toast.error(`${verworfen} Aufnahme(n) konnten nicht ausgewertet werden und wurden verworfen.`);
    }
  } finally {
    isProcessing = false;
  }
}

export function initFeedbackQueueSync(): void {
  window.addEventListener('online', () => { void processFeedbackQueue(); });
  if (navigator.onLine) void processFeedbackQueue();
}
